// Admin: everyone on this server, what they use it for, and what it is
// holding. Faults lead, because they are the one thing here that means
// somebody hit a bug.

import { getJson } from "../app/api.ts";
import { dayLabel, fmtAgo, fmtBytes, fmtCount, fmtDate, fmtSpan, fmtTime } from "../app/format.ts";
import { Icon } from "../app/icons.tsx";
import { follow } from "../app/router.ts";
import { session } from "../app/session.ts";
import { Pending, ScreenHead, useLoad } from "../app/ui.tsx";
import type { ReactNode } from "react";

type Any = Record<string, any>;

const Tile = ({ value, label, sub }: { value: ReactNode; label: string; sub?: ReactNode }) => (
  <div className="admin-tile">
    <div className="admin-tile-value num">{value}</div>
    <div className="admin-tile-label">{label}</div>
    <div className="admin-tile-sub">{sub || ""}</div>
  </div>
);

const Fact = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="admin-fact">
    <span className="admin-fact-label">{label}</span>
    <span className="admin-fact-value">{value ?? "—"}</span>
  </div>
);

export function AdminScreen() {
  const [data, reload] = useLoad(() => getJson<Any>("/api/admin"));
  const d = data.state === "ready" ? data.data : null;
  return (
    <div className="screen admin-screen">
      <ScreenHead
        title="Admin"
        subtitle="Everyone on this server, what they use it for, and what it is holding."
        actions={
          <button type="button" className="btn" onClick={reload}>
            <Icon name="refresh" />
            Refresh
          </button>
        }
      />
      {!d ? (
        <Pending value={data} text="Reading the server…" />
      ) : (
        <>
          <Faults groups={d.faults || []} now={d.now} />
          <Server d={d} />
          <h2 className="admin-heading">People</h2>
          {(d.users || []).map((p: Any) => (
            <Person key={p.email} p={p} now={d.now} signIn={Boolean(d.server?.sign_in)} />
          ))}
          <Work w={d.work || {}} />
          <Storage st={d.storage || {}} />
          <Recent rows={d.recent || []} now={d.now} />
        </>
      )}
    </div>
  );
}

/** A month of one count, a bar a day. The tallest day sets the scale, so the
 *  shape is what this account's month looked like. A bucket is named by its
 *  middle, which is the right date anywhere. */
function Spark({ values, fromDay, name }: { values: number[]; fromDay: number; name: string }) {
  const total = values.reduce((a, b) => a + b, 0);
  const top = Math.max(1, ...values);
  const step = 5;
  const height = 30;
  return (
    <div className="admin-spark">
      <div className="admin-spark-head">
        <span>{name}</span>
        <span className="num faint">
          {fmtCount(total)} in {values.length} days
        </span>
      </div>
      <svg viewBox={`0 0 ${values.length * step} ${height}`} preserveAspectRatio="none" role="img" aria-label={`${name}, one bar a day for the last ${values.length} days`}>
        {values.map((v, i) => {
          const bar = v ? Math.max(2, (v / top) * height) : 1;
          return (
            <rect key={i} className={v ? "on" : "off"} x={i * step} y={height - bar} width={step - 1} height={bar}>
              <title>
                {dayLabel((fromDay + i) * 86400 + 43200)}: {fmtCount(v)}
              </title>
            </rect>
          );
        })}
      </svg>
    </div>
  );
}

function Person({ p, now, signIn }: { p: Any; now: number; signIn: boolean }) {
  const speeds = Object.entries((p.by_speed || {}) as Record<string, number>)
    .sort((a, b) => b[1] - a[1])
    .map(([speed, count]) => `${speed} ${fmtCount(count)}`)
    .join(" · ");
  const puzzles = p.tactics + p.only_moves + p.blunders;
  const watching = !p.lichess_username ? "Not watching anyone." : `Watching ${p.lichess_username}${p.backfill_done ? "" : ", still walking back through history"}.`;
  return (
    <div className="card admin-person">
      <div className="admin-person-head">
        <div>
          <div className="admin-email">{p.email}</div>
          <div className="row-sub">{watching}</div>
        </div>
        <div className="admin-badges">
          {p.email === session.email ? <span className="badge solved">You</span> : null}
          {p.admin ? <span className="badge opening">Admin</span> : null}
          {signIn && !p.allowed ? <span className="badge blunder">Cannot sign in</span> : null}
          {p.sessions ? <span className="badge neutral">{p.sessions} signed in</span> : null}
        </div>
      </div>
      <div className="admin-tiles">
        <Tile value={fmtCount(p.games)} label="Games" sub={speeds} />
        <Tile value={fmtCount(puzzles)} label="Found in them" sub={`${fmtCount(p.tactics)} tactics · ${fmtCount(p.only_moves)} only moves · ${fmtCount(p.blunders)} blunders`} />
        <Tile value={fmtCount(p.waiting_puzzles)} label="Waiting" sub={`in ${fmtCount(p.waiting_games)} games`} />
        <Tile value={fmtCount(p.solved)} label="Solved" sub={`${fmtCount(p.solved_7)} this week · ${fmtCount(p.solved_30)} this month`} />
        <Tile value={fmtCount(p.repertoires)} label="Repertoires" sub={`${fmtCount(p.cards)} cards · ${fmtCount(p.cards_due)} due · ${fmtCount(p.cards_learned)} learned`} />
        <Tile value={fmtBytes(p.bytes)} label="Data" sub={`${fmtCount(p.reports)} analyses stored`} />
      </div>
      <div className="admin-sparks">
        <Spark values={p.activity.analysed} fromDay={p.activity.from_day} name="Games analysed" />
        <Spark values={p.activity.solved} fromDay={p.activity.from_day} name="Puzzles solved" />
      </div>
      <div className="admin-facts">
        <Fact label="Last analysis" value={fmtAgo(p.last_analysed_at, now)} />
        <Fact label="Last solved" value={fmtAgo(p.last_solved_at, now)} />
        <Fact label="Last drilled" value={fmtAgo(p.last_drilled_at, now)} />
        <Fact label="Last signed in" value={fmtAgo(p.last_signed_in, now)} />
        <Fact label="Games played" value={p.first_played_at ? `${fmtDate(p.first_played_at)} – ${fmtDate(p.last_played_at)}` : "—"} />
        <Fact label="Newest game seen" value={p.last_game_at ? fmtDate(Math.floor(p.last_game_at / 1000)) : "—"} />
        <Fact label="Opening analyses" value={fmtCount(p.opening_reports)} />
        <Fact label="Data" value={`${fmtBytes(p.report_bytes)} reports · ${fmtBytes(p.opening_bytes)} openings · ${fmtBytes(p.repertoire_bytes)} repertoires`} />
      </div>
    </div>
  );
}

function Server({ d }: { d: Any }) {
  const s = d.server || {};
  const users: Any[] = d.users || [];
  const sum = (pick: (u: Any) => number) => users.reduce((total, u) => total + pick(u), 0);
  const book = s.book;
  return (
    <div className="card">
      <h2>This server</h2>
      <div className="admin-tiles">
        <Tile value={fmtCount(users.length)} label="Accounts" sub={s.sign_in ? `${fmtCount(s.allowed_count)} may sign in` : "sign-in is off"} />
        <Tile value={fmtCount(sum((u) => u.games))} label="Games analysed" sub={`${fmtCount(sum((u) => u.reports))} analyses stored`} />
        <Tile
          value={fmtCount(sum((u) => u.tactics + u.only_moves + u.blunders))}
          label="Puzzles found"
          sub={`${fmtCount(sum((u) => u.solved))} solved · ${fmtCount(sum((u) => u.waiting_puzzles))} waiting`}
        />
        <Tile value={fmtBytes(d.storage?.file_bytes)} label="Database" sub={d.storage?.path || ""} />
        <Tile value={fmtSpan(s.uptime_seconds)} label="Up" sub={`since ${fmtDate(s.started_at)} ${fmtTime(s.started_at)}`} />
        <Tile
          value={fmtCount(s.queue?.pending)}
          label="In the queue"
          sub={[s.queue?.backfilling ? "backfilling" : null, s.queue?.full ? "full" : null, `${fmtCount(s.queue?.remembered)} remembered`].filter(Boolean).join(" · ")}
        />
      </div>
      <div className="admin-facts">
        <Fact label="Engine" value={s.engine} />
        <Fact label="Build" value={s.build} />
        <Fact label="Versions" value={`analysis ${s.analysis_version} · tags ${s.tags_version} · openings ${s.opening_analysis_version}`} />
        <Fact label="Rate limit" value={`${fmtCount(s.rate_limit)} requests per address every 10 minutes`} />
        <Fact label="Opening book" value={book ? `${fmtCount(book.positions)} positions · ${fmtCount(book.evaluated)} evaluated · built ${fmtDate(Number(book.built_at))}` : "not installed"} />
        <Fact
          label="Positions cached"
          value={`${fmtCount((d.storage?.tables || []).find((t: Any) => t.name === "engine_positions")?.rows)}, last ${fmtAgo(d.storage?.engine_positions_at, d.now)}`}
        />
      </div>
    </div>
  );
}

/** What broke in people's browsers this week, one row per distinct thing. */
function Faults({ groups, now }: { groups: Any[]; now: number }) {
  if (!groups.length) {
    return (
      <div className="card admin-faults clear">
        <h2>Faults this week</h2>
        <p className="muted">None. Every board and page that reported in played everything it was handed.</p>
      </div>
    );
  }
  const total = groups.reduce((sum, g) => sum + g.count, 0);
  return (
    <div className="card admin-faults">
      <h2>Faults this week: {fmtCount(total)}</h2>
      <p className="muted">
        Things pages reported going wrong: a move the board was handed and could not play, a line that stopped short, a script that threw. The same thing twice is one
        row.
      </p>
      {groups.map((g, i) => {
        const context: Any = g.context && typeof g.context === "object" ? g.context : {};
        const facts = [
          context.fen ? ["Position", context.fen] : null,
          context.moves ? ["Moves", context.moves] : null,
          context.board ? ["Board", context.board] : null,
          g.build ? ["Build", g.build] : null,
          context.agent ? ["Browser", context.agent] : null,
        ].filter((f): f is string[] => Boolean(f));
        return (
          <div key={i} className="admin-fault">
            <div className="admin-fault-head">
              <span className="admin-fault-kind">{g.kind}</span>
              <span className="num faint">
                {fmtCount(g.count)}× · {fmtCount(g.accounts)} account{g.accounts === 1 ? "" : "s"} · last {fmtAgo(g.last_at, now)}
              </span>
            </div>
            <div className="admin-fault-detail">{g.detail}</div>
            <details>
              <summary>
                {g.place ? (
                  <>
                    On <span className="admin-fault-place">{g.place}</span>
                  </>
                ) : (
                  "Where"
                )}
              </summary>
              {g.place ? (
                <p>
                  <a {...follow(g.place)}>Open that page</a>
                </p>
              ) : null}
              {facts.map(([label, value]) => (
                <Fact key={label} label={label!} value={value} />
              ))}
            </details>
          </div>
        );
      })}
    </div>
  );
}

/** What the background loops still owe, each the backlog of one pass. */
function Work({ w }: { w: Any }) {
  const jobs: [string, number, string][] = [
    ["Reports to tag again", w.retag, "Their puzzles were tagged by an older reading of the motifs."],
    ["Reports to check for continuations", w.continuations, "Puzzles that end a move too early get the rest of the line."],
    ["Opening analyses to redo", w.opening_analysis, `Computed against something other than ${w.opening_version}.`],
    ["Games whose moves are missing", w.moves_missing, "Nothing can be said about their openings until Lichess is asked for the moves."],
  ];
  const left = jobs.filter(([, n]) => n > 0);
  return (
    <div className="card">
      <h2>Background work</h2>
      {left.length ? (
        left.map(([label, n, why]) => (
          <div key={label} className="row">
            <div className="row-main">
              <div className="row-title">{label}</div>
              <div className="row-sub">{why}</div>
            </div>
            <div className="row-ctl num admin-work-count">{fmtCount(n)}</div>
          </div>
        ))
      ) : (
        <p className="muted">Everything stored is up to date with the code that is running.</p>
      )}
    </div>
  );
}

function Storage({ st }: { st: Any }) {
  const tables: Any[] = st.tables || [];
  const widest = Math.max(1, ...tables.map((t) => t.bytes));
  return (
    <div className="card">
      <h2>Storage</h2>
      <p className="muted">
        {fmtBytes(st.file_bytes)} on disk, counting the write-ahead log. The bars are the text that makes each table big — the reports, the moves, the trees — not the
        whole row.
      </p>
      <div className="admin-tables">
        {tables.map((t) => (
          <div key={t.name} className="admin-table-row">
            <div className="admin-table-name">
              {t.name}
              {t.owner === "shared" ? <span className="faint"> shared</span> : null}
            </div>
            <div className="admin-table-rows num">{fmtCount(t.rows)} rows</div>
            <div className="admin-table-bar">
              <div className="admin-table-fill" style={{ width: `${Math.round((100 * t.bytes) / widest)}%` }} />
            </div>
            <div className="admin-table-bytes num">{t.bytes ? fmtBytes(t.bytes) : "—"}</div>
          </div>
        ))}
      </div>
      {st.unowned_reports ? <p className="muted">{fmtCount(st.unowned_reports)} reports have no owner: they predate ownership, and anyone signed in can read them.</p> : null}
    </div>
  );
}

function Recent({ rows, now }: { rows: Any[]; now: number }) {
  return (
    <div className="card">
      <h2>Recently analysed</h2>
      <div className="admin-recent">
        {rows.map((r, i) => {
          const found = r.tactics + r.only_moves + r.blunders;
          const name = `${r.white} – ${r.black}`;
          return (
            <div key={i} className="admin-recent-row">
              <div className="admin-recent-when">{fmtAgo(r.analysed_at, now)}</div>
              <div className="admin-recent-who faint">{r.email || "no owner"}</div>
              <div className="admin-recent-game">
                {r.email === session.email && r.game_id ? <a {...follow(`/games/${r.game_id}/${r.color}`)}>{name}</a> : <span>{name}</span>}
                <span className="faint">
                  {" "}
                  {r.speed || ""} {r.color}
                </span>
              </div>
              <div className="admin-recent-found num">{found ? `${fmtCount(found)} found` : "nothing"}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
